import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Factory, GraduationCap, Briefcase, Building2, Phone, Mail, ChevronDown } from "lucide-react";
import { Reveal, MaskedLines, Counter, SectionHead, GoldButton, OutlineButton } from "@/components/bits/Reveal";
import { IMAGES, STATS, PILLARS, SERVICES, INDUSTRIES, CLIENTS, STUDENT_CATEGORIES, STUDENT_ROLES, CONTACT } from "@/data/content";
import { useSEO } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center bg-darknavy noise-bg overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, opacity }}>
        <img src={IMAGES.hero} alt="Industrial workforce" className="w-full h-full object-cover opacity-35" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-darknavy via-darknavy/85 to-darknavy/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-darknavy via-transparent to-darknavy/60" />
      </motion.div>

      <div className="absolute right-0 top-0 h-full w-1/2 pointer-events-none hidden md:block">
        <motion.div className="absolute top-32 right-24 w-px h-56 bg-gradient-to-b from-transparent via-gold/50 to-transparent" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.4, delay: 1.2 }} style={{ transformOrigin: "top" }} />
        <motion.div className="absolute top-40 right-40 w-px h-80 bg-gradient-to-b from-transparent via-gold/25 to-transparent" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.6, delay: 1.4 }} style={{ transformOrigin: "top" }} />
        <motion.div className="absolute top-24 right-24 w-56 h-56 border border-gold/15 rounded-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, delay: 1.1 }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-28 w-full">
        <motion.p
          className="text-xs sm:text-sm uppercase tracking-[0.35em] font-bold text-lightgold mb-7"
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
        >
          Maqline Solutions Pvt Ltd
        </motion.p>

        <MaskedLines
          lines={["Building Stronger Workforces.", "Powering Better Businesses."]}
          delay={0.4}
          className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl font-medium tracking-tight text-white max-w-4xl text-balance"
          lineClassName="last-line"
        />

        <motion.p
          className="mt-6 text-lg sm:text-xl text-lightgold/90 font-quote italic"
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
        >
          Connecting businesses with dependable talent — and students with meaningful opportunities.
        </motion.p>

        <motion.p
          className="mt-4 max-w-2xl text-sm sm:text-base text-white/60 leading-relaxed"
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
        >
          Maqline Solutions provides manpower outsourcing, recruitment, contract staffing, internship,
          industrial training and workforce solutions for manufacturing companies, MNCs and growing organizations.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
        >
          <Link to="/businesses#request" data-testid="hero-request-manpower">
            <button className="btn-sweep inline-flex items-center justify-center gap-2 bg-gold text-darknavy font-semibold text-sm tracking-wide px-7 py-3.5 rounded-sm hover:bg-lightgold transition-colors duration-300">
              Request Manpower <ArrowRight size={16} />
            </button>
          </Link>
          <Link to="/careers" data-testid="hero-explore-careers">
            <OutlineButton dark>Explore Careers</OutlineButton>
          </Link>
          <Link to="/internships" data-testid="hero-internships-link" className="gold-underline text-sm text-white/70 hover:text-lightgold transition-colors duration-300 font-semibold">
            Looking for Internships?
          </Link>
        </motion.div>

        <motion.div
          className="mt-16 flex items-center gap-8 text-white/40"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.8 }}
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]">
            <span className="w-10 h-px bg-gold/60" /> Est. 2026
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]">
            <span className="w-10 h-px bg-gold/60" /> Manpower Outsourcing & Workforce Solutions
          </div>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40"
        animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
}

function AudienceCards() {
  const cards = [
    {
      icon: Factory, tag: "For Businesses", title: "Need Reliable Manpower?",
      text: "Find dependable workforce solutions for your production, manufacturing and operational requirements.",
      img: IMAGES.audienceBusiness,
      ctas: [{ label: "Request Manpower", to: "/businesses#request", primary: true }, { label: "Talk to Our Team", to: "/contact" }],
      testId: "audience-businesses",
    },
    {
      icon: GraduationCap, tag: "For Students & Job Seekers", title: "Looking for Your Next Opportunity?",
      text: "Explore jobs, internships, industrial training and career opportunities connected to the industry.",
      img: IMAGES.audienceStudents,
      ctas: [{ label: "Explore Careers", to: "/careers", primary: true }, { label: "Apply for Internship", to: "/internships" }],
      testId: "audience-students",
    },
    {
      icon: Building2, tag: "For Colleges & TPOs", title: "Connect Your Students With Industry",
      text: "Partner with Maqline for internships, industrial training, placement opportunities and industry exposure.",
      img: IMAGES.audienceColleges,
      ctas: [{ label: "Partner With Us", to: "/colleges", primary: true }, { label: "Explore Student Programs", to: "/internships#training" }],
      testId: "audience-colleges",
    },
  ];

  return (
    <section className="relative bg-steel py-16 sm:py-24" data-testid="audience-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          overline="How Can We Help You?"
          title={["Three audiences.", "One workforce ecosystem."]}
          description="Whether you run a plant, hunt for your first job, or lead a placement cell — this is where your journey with Maqline begins."
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <Reveal key={c.tag} delay={i * 0.12}>
              <div
                data-testid={c.testId}
                className="group relative bg-white border border-ink/8 hover:border-gold/50 rounded-sm overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(6,27,58,0.12)] h-full flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={c.img} alt={c.tag} loading="lazy" className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-darknavy/70 to-transparent" />
                  <div className="absolute bottom-4 left-5 flex items-center gap-2.5">
                    <c.icon size={18} className="text-lightgold" />
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-white">{c.tag}</span>
                  </div>
                </div>
                <div className="p-6 sm:p-7 flex flex-col flex-1">
                  <h3 className="font-display text-2xl text-ink mb-3">{c.title}</h3>
                  <p className="text-sm text-muted leading-relaxed flex-1">{c.text}</p>
                  <div className="mt-6 flex flex-col gap-2.5">
                    {c.ctas.map((cta) => (
                      <Link key={cta.label} to={cta.to} data-testid={`${c.testId}-${cta.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                        {cta.primary ? (
                          <GoldButton className="w-full text-[13px] py-3">{cta.label}</GoldButton>
                        ) : (
                          <OutlineButton className="w-full text-[13px] py-3">{cta.label}</OutlineButton>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-gold to-lightgold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="relative bg-darknavy noise-bg py-16 sm:py-20" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <SectionHead dark overline="Our Track Record" title={["Numbers that carry", "the weight of trust."]} />
          <Reveal delay={0.2}>
            <p className="text-white/50 text-sm max-w-xs lg:text-right">Connecting talent with organizations across the industrial workforce ecosystem.</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STATS.map((s, i) => (
            <div key={s.label} className="bg-darknavy p-7 sm:p-9 group hover:bg-[#0a2450] transition-colors duration-500" data-testid={`stat-${i}`}>
              <Reveal delay={i * 0.08}>
                <p className="font-mono text-4xl sm:text-5xl font-bold text-lightgold">
                  {s.raw ? s.value : <Counter value={s.value} suffix={s.suffix} />}
                </p>
                <p className="mt-3 text-sm font-semibold text-white">{s.label}</p>
                <p className="mt-1 text-xs text-white/45 leading-relaxed">{s.note}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="about-preview">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            overline="About Maqline"
            title={["Connecting Talent,", "Industry and Opportunity"]}
            description="Maqline Solutions Pvt Ltd is a workforce solutions company focused on bridging the gap between organizational manpower requirements and available talent."
          />
          <Reveal delay={0.25}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {PILLARS.map((p) => (
                <span key={p.num} className="inline-flex items-center gap-2 text-sm font-semibold text-navy bg-navy/5 border border-navy/15 px-4 py-2 rounded-sm">
                  <span className="font-mono text-[11px] text-gold">{p.num}</span> {p.title}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <Link to="/about" data-testid="about-preview-link" className="mt-9 inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-gold transition-colors duration-300 uppercase tracking-[0.15em]">
              Our Full Story <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <img src={IMAGES.factory} alt="Industrial facility" loading="lazy" className="w-full h-[400px] object-cover rounded-sm" />
            <div className="absolute inset-0 border border-gold/25 rounded-sm translate-x-4 translate-y-4 -z-10" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="bg-steel py-16 sm:py-24" data-testid="services-preview">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <SectionHead overline="What We Do" title={["Workforce services,", "end to end."]} description="From sourcing a single technician to deploying an entire production line — every service is built around dependable delivery." />
          <Reveal delay={0.2}>
            <Link to="/services" data-testid="services-view-all"><OutlineButton>All Services <ArrowRight size={15} /></OutlineButton></Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 4) * 0.08}>
              <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block bg-white border border-ink/8 hover:border-gold/50 rounded-sm p-6 h-full transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(6,27,58,0.1)]">
                <span className="font-mono text-xs text-gold font-bold">{s.num}</span>
                <h3 className="font-display text-lg text-ink mt-3 mb-2 group-hover:text-navy transition-colors duration-300 leading-snug">{s.title}</h3>
                <p className="text-[13px] text-muted leading-relaxed">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-navy group-hover:text-gold transition-colors duration-300 uppercase tracking-wider">
                  Details <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BusinessStrip() {
  return (
    <section className="relative bg-navy noise-bg py-16 sm:py-20 overflow-hidden" data-testid="business-strip">
      <div className="absolute -right-24 top-0 w-[480px] h-full opacity-10 hidden lg:block">
        <img src={IMAGES.factory} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <SectionHead dark overline="For Employers" title={["Your Workforce Requirements.", "Our Recruitment Support."]} description="From a single technician to an entire production line — share your requirement and Maqline handles sourcing, screening, deployment and coordination." />
        <Reveal delay={0.15}>
          <div className="space-y-4">
            {["Structured six-step engagement from requirement to deployment", "600+ candidates deployed across our client network", "Ongoing workforce coordination after joining day"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm text-white/75">
                <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /> {t}
              </div>
            ))}
            <div className="pt-4 flex flex-wrap gap-3">
              <Link to="/businesses" data-testid="business-strip-cta"><GoldButton gold>For Businesses <ArrowRight size={15} /></GoldButton></Link>
              <Link to="/process" data-testid="business-strip-process"><OutlineButton dark>See the Process</OutlineButton></Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StudentStrip() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="student-strip">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                <img src={IMAGES.students} alt="Students in a technical environment" loading="lazy" className="w-full h-[420px] object-cover rounded-sm" />
                <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-darknavy text-white p-6 rounded-sm border border-gold/30 shadow-[0_20px_50px_rgba(6,27,58,0.35)]">
                  <p className="font-mono text-4xl font-bold text-lightgold">500+</p>
                  <p className="text-xs uppercase tracking-[0.18em] text-white/70 mt-1">Job Opportunities Available</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHead
              overline="For Students & Job Seekers"
              title={["Your career starts with", "the right opportunity."]}
              description="Explore current job opportunities across manufacturing, automotive, engineering, electronics and other industrial sectors — plus internships and industrial training for students."
            />
            <Reveal delay={0.2}>
              <div className="mt-7">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold mb-3">Who can apply</p>
                <div className="flex flex-wrap gap-2">
                  {STUDENT_CATEGORIES.map((c) => (
                    <span key={c} className="text-xs font-semibold text-navy bg-navy/5 border border-navy/15 px-3 py-1.5 rounded-sm">{c}</span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold mb-3">Roles we hire for</p>
                <div className="flex flex-wrap gap-2">
                  {STUDENT_ROLES.map((r) => (
                    <span key={r} className="text-xs text-muted border border-ink/10 px-3 py-1.5 rounded-sm">{r}</span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/careers" data-testid="student-strip-jobs"><GoldButton>View All Jobs <ArrowRight size={15} /></GoldButton></Link>
                <Link to="/careers#profile" data-testid="student-strip-profile"><OutlineButton>Submit Your Profile</OutlineButton></Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function CollegeStrip() {
  const items = ["Internship Programs", "Industrial Training", "Placement Support", "Recruitment Drives", "Student Career Opportunities", "Industry Exposure", "Candidate Registration", "Campus Engagement"];
  return (
    <section className="bg-steel py-16 sm:py-24" data-testid="college-strip">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            overline="For Colleges & TPOs"
            title={["Bridging the gap between", "education and industry."]}
            description="Maqline Solutions works with educational institutions to create structured pathways for students through internships, industrial training, placement opportunities, recruitment drives and industry exposure."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 grid grid-cols-2 gap-2.5">
              {items.map((it) => (
                <div key={it} className="flex items-center gap-2.5 text-sm text-ink/80">
                  <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /> {it}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9">
              <Link to="/colleges" data-testid="college-strip-cta"><GoldButton>Partner With Maqline <ArrowRight size={15} /></GoldButton></Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <img src={IMAGES.audienceColleges} alt="Student in an industrial lab" loading="lazy" className="w-full h-[440px] object-cover rounded-sm" />
            <div className="absolute inset-0 border border-gold/25 rounded-sm translate-x-4 translate-y-4 -z-10" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function IndustriesGrid() {
  return (
    <section className="bg-darknavy noise-bg py-16 sm:py-20" data-testid="industries-preview">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead dark overline="Industries We Serve" title={["Built for the industrial", "economy of India."]} />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-0">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.num} delay={(i % 3) * 0.06}>
              <Link
                to="/industries"
                data-testid={`industry-${ind.num}`}
                className="group flex items-center justify-between py-4 border-b border-white/10 hover:border-gold/40 transition-colors duration-300"
              >
                <span className="flex items-center gap-4">
                  <span className="font-mono text-xs text-gold/70 font-bold">{ind.num}</span>
                  <span className="font-display text-lg text-white/85 group-hover:text-lightgold transition-colors duration-300">{ind.name}</span>
                </span>
                <ArrowRight size={15} className="text-white/20 group-hover:text-lightgold group-hover:translate-x-1 transition-all duration-300" />
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/industries" data-testid="industries-explore" className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-lightgold hover:text-white transition-colors duration-300 uppercase tracking-[0.15em]">
            Industry-wise workforce details <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ClientMarquee() {
  return (
    <section className="bg-white py-12 sm:py-16 border-b border-ink/8" id="clients" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-10">
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-gold text-center" data-testid="clients-overline">Associated Companies & Client Network</p>
        <h2 className="font-display text-2xl sm:text-3xl text-ink text-center mt-3">Organizations Across the Industrial Ecosystem</h2>
      </div>
      <div className="relative overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
          {[...CLIENTS, ...CLIENTS].map((c, i) => (
            <div key={i} className="flex items-center gap-10 px-10" data-testid={i < CLIENTS.length ? `client-${c.toLowerCase().replace(/[^a-z]/g, "-")}` : undefined}>
              <span className="font-display text-lg sm:text-xl text-ink/45 hover:text-navy whitespace-nowrap transition-colors duration-300 uppercase tracking-wide">{c}</span>
              <span className="w-1.5 h-1.5 bg-gold/60 rotate-45 shrink-0" />
            </div>
          ))}
        </div>
      </div>
      <p className="text-center text-xs text-muted mt-8 px-6">Client network includes organizations across automotive, electronics, manufacturing, defence, solar and precision components.</p>
    </section>
  );
}

function CtaBand() {
  const bands = [
    { tag: "Employers", title: "Have a manpower requirement?", cta: "Request Manpower", to: "/businesses#request", icon: Briefcase, testId: "cta-band-employer" },
    { tag: "Students", title: "Ready to find your opportunity?", cta: "Explore 500+ Openings", to: "/careers", icon: GraduationCap, testId: "cta-band-student" },
    { tag: "Colleges", title: "Want industry exposure for students?", cta: "Partner With Us", to: "/colleges", icon: Building2, testId: "cta-band-college" },
  ];
  return (
    <section className="bg-white py-16 sm:py-20" data-testid="cta-bands">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {bands.map((b, i) => (
          <Reveal key={b.tag} delay={i * 0.1}>
            <div className="group relative bg-darknavy noise-bg rounded-sm p-8 overflow-hidden h-full" data-testid={b.testId}>
              <div className="absolute -right-8 -bottom-8 opacity-[0.07] group-hover:opacity-[0.14] transition-opacity duration-500">
                <b.icon size={150} className="text-white" />
              </div>
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-lightgold">{b.tag}</p>
              <h3 className="font-display text-2xl text-white mt-3 mb-7 max-w-[240px]">{b.title}</h3>
              <Link to={b.to} data-testid={`${b.testId}-button`}>
                <GoldButton gold className="text-[13px]">{b.cta} <ArrowRight size={14} /></GoldButton>
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ContactStrip() {
  return (
    <section className="bg-steel py-14 sm:py-20" data-testid="contact-strip">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-gold mb-3">Talk To Us</p>
          <h2 className="font-display text-3xl sm:text-4xl text-ink">Let's build your workforce story.</h2>
          <p className="text-muted mt-3 text-sm max-w-lg">{CONTACT.address}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={`mailto:${CONTACT.email}`} data-testid="contact-strip-email"><GoldButton><Mail size={15} /> Email Us</GoldButton></a>
          <Link to="/contact" data-testid="contact-strip-page"><OutlineButton>Contact Page <ArrowRight size={15} /></OutlineButton></Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Maqline Solutions Pvt Ltd | Manpower Outsourcing & Workforce Solutions, Bengaluru",
    "Manpower outsourcing, contract staffing, recruitment, internships and industrial training in Bengaluru. 600+ candidates deployed across our client network. 500+ job openings for ITI, Diploma and fresher candidates."
  );
  return (
    <main data-testid="home-page">
      <Hero />
      <AudienceCards />
      <ClientMarquee />
      <StatsSection />
      <AboutPreview />
      <ServicesPreview />
      <BusinessStrip />
      <StudentStrip />
      <CollegeStrip />
      <IndustriesGrid />
      <CtaBand />
      <ContactStrip />
    </main>
  );
}
